/**
 * workspace-dependencies.ts
 *
 * Topological Plugin Dependency Graph Resolver (Sprint ONBOARD-01):
 * - Evaluates Directed Acyclic Graph (DAG) using Kahn's algorithm.
 * - Automatically resolves and auto-adds required dependencies.
 * - Tracks missing optional dependencies with graceful capability degradation.
 * - Detects conflicts and declares cycle rejection (ERR_CIRCULAR_DEPENDENCY).
 */

import { CANONICAL_18_PLUGIN_CATALOG, type PluginManifest } from './plugin-manifests.js';

export interface DependencyResolutionResult {
  readonly isValid: boolean;
  readonly resolvedPluginIds: readonly string[];
  readonly autoAddedDependencies: readonly {
    readonly pluginId: string;
    readonly requiredBy: string;
  }[];
  readonly missingOptionalDependencies: readonly {
    readonly pluginId: string;
    readonly optionalFor: string;
  }[];
  readonly conflictsDetected: readonly {
    readonly pluginA: string;
    readonly pluginB: string;
    readonly reason: string;
  }[];
  readonly circularDependencyCycle?: readonly string[];
  readonly loadOrder: readonly string[];
}

export class WorkspaceDependencyResolver {
  private readonly catalogMap = new Map<string, PluginManifest>();

  constructor(customCatalog?: readonly PluginManifest[]) {
    const catalog = customCatalog ?? CANONICAL_18_PLUGIN_CATALOG;
    for (const manifest of catalog) {
      this.catalogMap.set(manifest.pluginId, manifest);
    }
  }

  public getManifest(pluginId: string): PluginManifest | undefined {
    return this.catalogMap.get(pluginId);
  }

  public listManifests(): readonly PluginManifest[] {
    return Array.from(this.catalogMap.values());
  }

  public resolve(selectedPluginIds: readonly string[]): DependencyResolutionResult {
    const autoAddedDependencies: { pluginId: string; requiredBy: string }[] = [];
    const missingOptional: { pluginId: string; optionalFor: string }[] = [];
    const conflicts: { pluginA: string; pluginB: string; reason: string }[] = [];

    const activeSet = new Set<string>(selectedPluginIds);

    // 1. Recursive Auto-Add Required Dependencies
    let changed = true;
    while (changed) {
      changed = false;
      for (const pluginId of Array.from(activeSet)) {
        const manifest = this.catalogMap.get(pluginId);
        if (!manifest) continue;

        for (const reqId of manifest.requiredDependencies) {
          if (!activeSet.has(reqId)) {
            activeSet.add(reqId);
            autoAddedDependencies.push({ pluginId: reqId, requiredBy: pluginId });
            changed = true;
          }
        }

        for (const optId of manifest.optionalDependencies) {
          if (!activeSet.has(optId)) {
            missingOptional.push({ pluginId: optId, optionalFor: pluginId });
          }
        }
      }
    }

    // 2. Conflict Detection
    for (const pluginId of activeSet) {
      const manifest = this.catalogMap.get(pluginId);
      if (!manifest) continue;

      for (const conflictId of manifest.conflictsWith) {
        if (activeSet.has(conflictId)) {
          conflicts.push({
            pluginA: pluginId,
            pluginB: conflictId,
            reason: `Direct manifest conflict declared between ${pluginId} and ${conflictId}`,
          });
        }
      }
    }

    if (conflicts.length > 0) {
      return {
        isValid: false,
        resolvedPluginIds: Array.from(activeSet),
        autoAddedDependencies,
        missingOptionalDependencies: missingOptional,
        conflictsDetected: conflicts,
        loadOrder: [],
      };
    }

    // 3. Topological Sort using Kahn's Algorithm
    const inDegree = new Map<string, number>();
    const adjList = new Map<string, string[]>();

    for (const pluginId of activeSet) {
      inDegree.set(pluginId, 0);
      adjList.set(pluginId, []);
    }

    for (const pluginId of activeSet) {
      const manifest = this.catalogMap.get(pluginId);
      if (!manifest) continue;

      for (const reqId of manifest.requiredDependencies) {
        if (activeSet.has(reqId)) {
          adjList.get(reqId)!.push(pluginId);
          inDegree.set(pluginId, (inDegree.get(pluginId) ?? 0) + 1);
        }
      }
    }

    const queue: string[] = [];
    for (const [pluginId, deg] of inDegree.entries()) {
      if (deg === 0) {
        queue.push(pluginId);
      }
    }

    const loadOrder: string[] = [];
    while (queue.length > 0) {
      const u = queue.shift()!;
      loadOrder.push(u);

      for (const v of adjList.get(u) ?? []) {
        const nextDeg = (inDegree.get(v) ?? 1) - 1;
        inDegree.set(v, nextDeg);
        if (nextDeg === 0) {
          queue.push(v);
        }
      }
    }

    if (loadOrder.length < activeSet.size) {
      const cycleNodes = Array.from(activeSet).filter((id) => !loadOrder.includes(id));
      return {
        isValid: false,
        resolvedPluginIds: Array.from(activeSet),
        autoAddedDependencies,
        missingOptionalDependencies: missingOptional,
        conflictsDetected: [],
        circularDependencyCycle: cycleNodes,
        loadOrder: [],
      };
    }

    return {
      isValid: true,
      resolvedPluginIds: Array.from(activeSet),
      autoAddedDependencies,
      missingOptionalDependencies: missingOptional,
      conflictsDetected: [],
      loadOrder,
    };
  }

  /**
   * Backward-compatible helper for legacy callers.
   */
  public resolveDependencies(enabledPluginIds: readonly string[]) {
    const enabledSet = new Set(enabledPluginIds);
    const missingRequired = new Map<string, string[]>();

    for (const pluginId of enabledPluginIds) {
      const manifest = this.catalogMap.get(pluginId);
      if (!manifest) continue;
      const missing: string[] = [];
      for (const reqId of manifest.requiredDependencies) {
        if (!enabledSet.has(reqId)) {
          missing.push(reqId);
        }
      }
      if (missing.length > 0) {
        missingRequired.set(pluginId, missing);
      }
    }

    const res = this.resolve(enabledPluginIds);
    const circularDependencies: string[][] = res.circularDependencyCycle ? [Array.from(res.circularDependencyCycle)] : [];

    return {
      valid: missingRequired.size === 0 && res.isValid,
      missingRequired,
      circularDependencies,
      loadOrder: Array.from(res.loadOrder),
    };
  }
}

export const globalDependencyResolver = new WorkspaceDependencyResolver();
